import { compact } from 'lodash'
import moment, { Moment } from 'moment-timezone'
import type { ReactElement } from 'react'
import { OptionType } from '../types/OptionType'
import { sanitizeValue } from './utils'

const isValueEmpty = (value: string | string[]): boolean => {
  return value.length === 0 || value === `` || !value || (Array.isArray(value) && value.every((val) => val === ``))
}

export const FILTER_SEPARATOR = `||`

/**
 * A filter that is pre-applied to the UI and ES query.
 * key - Column name, a.k.a, attribute. Eg. `patientStatus`
 * value - The value to filter on. Eg. `Active`
 * operator - The operator to use for the filter. Eg. `exactly`
 */
export type PredefinedFilter = Record<string, { value?: any; operator: FilterOperator; readOnly?: boolean }>

/**
 * A saved list's rule as the table reads it: one filter per attribute, the last row on an
 * attribute winning. Every other reader of the same rule goes through this too, so they agree.
 */
export function filtersByAttribute(rows: readonly FilterObject[]): PredefinedFilter {
  return rows.reduce<PredefinedFilter>(
    (acc, filter) => ({ ...acc, [filter.attribute]: { value: filter.value, operator: filter.operator } }),
    {},
  )
}

/**
 * @type - NoDefaultValueTypes. FilterValueTypes that do not require a default value. Eg. FilterValueType.string
 */
type NoDefaultValueTypes = keyof { [K in FilterValueType]: K extends FilterValueType.rangeDate ? never : K }
/**
 * @type TypeDefaultValue. Filter that require a default value and other props to work correctly. Eg. FilterValueType.rangeDate
 * @type type - The type of the filter. If FilterValueType.rangeDate is used then dateOperator or defaultValue is required.
 * @type defaultValue - The default value to use for the filter.
 * @type dateOperator - The default date operator to use for the filter.

 */
type TypeDefaultValue = {
  type: NoDefaultValueTypes
  defaultValue?: string | string[]
  dateOperator?: DateRangeOperator
}

/**
 * @type DateRangeSelectorOptions. Options for the date range selector.
 * @type includeToday - If true, the filter will include Today selector in the date range.
 * @type includeYesterday - If true, the filter will include Yesterday selector in the date range.
 * @type includeWeeks - If true, the filter will include includeWeeks in the date range.
 * @type limitToMonth - If true, the filter will limit the date range to months.
 * @type includeInThePast - If true, the filter will include the open-ended In the past selector.
 * @type includeFuture - If true, the filter will include the forward looking selectors
 * (Next 30 Days, Next 90 Days, In the future). Only meaningful on columns that can hold a
 * date later than today, eg. due dates and upcoming appointments.
 */
type DateRangeSelectorOptions = {
  includeToday?: boolean
  includeYesterday?: boolean
  includeWeeks?: boolean
  limitToMonth?: boolean
  includeInThePast?: boolean
  includeFuture?: boolean
}

/* eslint-disable quotes */
export type FilterObject = {
  attribute: string
  secondaryAttribute?: string
  displayName: string
  operator: FilterOperator
  value?: string | string[]
  secondaryValue?: string[]
  options?: OptionType[]
  static?: boolean
  uniqOperator?: FilterOperator
  readOnly?: boolean
  nonEmpty?: boolean
  useAsterisk?: boolean
  group?: string
  subQuery?: string
  optionsMapper?: (response: any) => OptionType[]
} & TypeDefaultValue &
  DateRangeSelectorOptions

export enum FilterValueType {
  'string',
  'id',
  'number',
  'rangeDate',
  'date',
  'boolean',
  'multiple',
  'stringArray',
  'age',
  'readOnly',
  'assignedTo',
  'attributeExists',
  'pmsIntegration',
  'asyncSelect',
  'asyncMultiSelect',
}

export type FilterValue = string | string[] | undefined
/**
 * A one-choice rule's option that takes a day count, stored as `<kind>:<days>` (e.g. `due:14`).
 * The row reads `<before> [days] <after>`; `label` is its line in the menu.
 */
export type DayWindowChoice = { kind: string; label: string; before: string; after: string; defaultDays: number }

/**
 * @type attribute - The name of the attribute to filter on. Eg. `patientStatus`
 * @type secondaryAttribute - It is used for filters that filter by attribute but show a different attribute in the UI. Eg. `assignedTo` but show `usedId` in the UI.
 * @type displayName - The name of the attribute to display in the UI. Eg. `Contact Status`
 * @type readOnly - If true, a filter can not change its operator and value. E.g `My Assigned Contacts`.
 * @type defaultFilter - If true, a filter came from the loader and should be rendered in the UI with pre-applied values.
 * @type customSearchParam - URL search param to use for the loader filter. Eg. `channel` or `toDate`.
 * @type queryByName - Query by the name of the contact owner instead of the id Eg. `leadOwner.name`.
 * @type defaultOperator - The default operator to use for the filter.
 * @type uniqOperator - If set, the filter will be unique in the UI and a user can not change the operator.
 * @type optionsSource - If set, API endpoint to search for filter values. Eg. `/api/locations/`
 * @type contextualFiltering - If set, the filter will hear for a pre-applied filter and restrict possible options.
 * It translates from a filter.attribute to a specific model field Eg. `{ utmAccountId: 'accountId' }`.
 * @type additionalParams - Additional params to be added to the API endpoint. Eg. `{ workspaceId: '1' }`
 * @type options - To be used with dropdown filters.
 * @type value - Takes precedence over defaultValue when it was set by the user. It is used in deep linking.
 * @type secondaryValue - It is used in deep linking.
 * @type operator - Takes precedence over defaultOperator when it was set by the user. It is used in deep linking.
 * @type nonEmpty - If true, the filter will be included in the ES querying any not empty value. Eg. utmCampaing:"*".
 * @type customComboOption - If set, the filter will use a custom component to render the filter. Eg. `text and icon`.
 * @type useAsterisk - If true, the filter will use an asterisk to represent all values. Eg. `*` for `Ad Platform`.
 * @type group - If set, the filter will be grouped in the UI. Eg. `Person` or `Company`.
 * @type standalone - If true, the filter will be standalone and not part of the main filters.
 * @type paramDefault - For a `customSearchParam` column whose page applies `defaultValue` while the param is
 * absent. The builder shows that default as a row whatever else the URL holds, and removing the row writes the
 * param empty instead of deleting it, so the page's default does not come straight back.
 **/
export type FilterableColumn = {
  attribute: string
  secondaryAttribute?: string
  displayName: string
  readOnly?: boolean
  defaultFilter?: boolean
  customSearchParam?: string
  queryByName?: boolean
  defaultOperator?: FilterOperator
  uniqOperator?: FilterOperator
  optionsSource?: string
  fetcher?: (params: { query: string; [key: string]: any }) => Promise<any>
  contextualFiltering?: Record<string, string>
  additionalParams?: { [key: string]: string }
  options?: OptionType[]
  value?: string | string[]
  secondaryValue?: string[]
  operator?: FilterOperator
  nonEmpty?: boolean
  useAsterisk?: boolean
  group?: string
  standalone?: boolean
  paramDefault?: boolean
  showAllText?: boolean
  includeAll?: boolean
  customComboOption?: ({ selected }: { selected: boolean }) => ReactElement
  optionsMapper?: (response: any) => OptionType[]
  // Render this filter's async options grouped under collapsible headers keyed on each
  // option's `meta.group` (e.g. group pipeline stages under their pipeline).
  groupOptions?: boolean
  subQuery?: string
  dayWindows?: ReadonlyArray<DayWindowChoice>
} & TypeDefaultValue &
  DateRangeSelectorOptions

export const isEmptyFilter = (type: FilterValueType, value: FilterValue) =>
  (type === FilterValueType.id || type === FilterValueType.stringArray || type === FilterValueType.multiple) &&
  (typeof value === 'undefined' || !value || compact(value).length === 0)

export const getAllowedOperators = (valueType: FilterValueType): FilterOperator[] => {
  switch (valueType) {
    case FilterValueType.id:
    case FilterValueType.stringArray:
    case FilterValueType.multiple:
      return [`in`, `not_in`]
    case FilterValueType.string:
      return [`contains`, `not_contains`, `exactly`]
    case FilterValueType.rangeDate:
      return [`between`]
    case FilterValueType.number:
    case FilterValueType.age:
      return [`exactly`, `greater_than`, `greater_equal_than`, `lesser_than`, `between`]
    case FilterValueType.readOnly:
    case FilterValueType.pmsIntegration:
      return [`exactly`]
    case FilterValueType.boolean:
    case FilterValueType.asyncSelect:
    case FilterValueType.asyncMultiSelect:
      return [`exactly`, `not_exactly`]
    case FilterValueType.attributeExists:
      return [`attribute_exists`]
    case FilterValueType.date:
      return [`greater_equal_than`, `exactly`, `not_contains`]
    default:
      return [`contains`]
  }
}
/* eslint-disable-line quotes */
export enum DateRangeOperator {
  'custom',
  'yesterday',
  'today',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_year',
  'last_year',
  'last_30_days',
  'last_90_days',
  'last_6_months',
  'last_12_months',
  'all',
  'in_the_past',
  'next_30_days',
  'next_90_days',
  'in_the_future',
  'today_or_later',
}

/**
 * Date presets with only one bound, so they cannot be frozen into a from/to pair.
 * Their value travels through the URL as the preset name and the window is
 * recomputed against the current date every time the query is built.
 * `today_or_later` differs from `in_the_future` by including today: an appointment
 * later today is still ahead of the person looking.
 */
export const INDEFINITE_DATE_PRESETS = [`in_the_past`, `in_the_future`, `today_or_later`] as const

export type IndefiniteDatePreset = (typeof INDEFINITE_DATE_PRESETS)[number]

export function isIndefiniteDatePreset(value: string | undefined): value is IndefiniteDatePreset {
  return !!value && INDEFINITE_DATE_PRESETS.includes(value as IndefiniteDatePreset)
}

export type FilterOperator =
  | `in`
  | `not_in`
  | `greater_than`
  | `greater_equal_than`
  | `lesser_than`
  | `between`
  | `contains`
  | `not_contains`
  | `exactly`
  | `not_exactly`
  | `attribute_exists`
  | `attribute_not_exists`
  | `in_the_past`
  | `in_the_future`
  | `gte`
  | `lte`

const filterOperators: readonly FilterOperator[] = [
  `in`,
  `not_in`,
  `greater_than`,
  `greater_equal_than`,
  `lesser_than`,
  `between`,
  `contains`,
  `not_contains`,
  `exactly`,
  `not_exactly`,
  `attribute_exists`,
  `attribute_not_exists`,
  `in_the_past`,
  `in_the_future`,
]

export const isFilterOperator = (value: string): value is FilterOperator => {
  return filterOperators.includes(value as FilterOperator)
}

export const toFilterValue = (value: unknown): FilterValue => {
  if (typeof value === `string`) return value
  if (Array.isArray(value)) {
    let strings = value.filter((item): item is string => typeof item === `string`)
    return strings.length === value.length ? strings : undefined
  }
  return undefined
}

export function getReadableOperator(op: FilterOperator, type: FilterValueType): string {
  switch (op) {
    case 'in':
      return `includes`
    case 'not_in':
      return 'does not include'
    case 'not_contains':
      return type === FilterValueType.date ? `≠` : `does not include`
    case 'greater_than':
      if (type === FilterValueType.number || type === FilterValueType.age) {
        return 'greater than'
      } else if (type === FilterValueType.date) {
        return `>`
      }
      return 'after'
    case 'greater_equal_than':
      return `≥`
    case 'lesser_than':
      return type === FilterValueType.number || type === FilterValueType.age ? 'less than' : 'before'
    case 'contains':
      return 'includes'
    case 'exactly':
      return type === FilterValueType.date ? `=` : `is`
    case 'not_exactly':
      return `is not`
    case 'attribute_exists':
      return 'is'
    case 'attribute_not_exists':
      return 'is not'
    case `between`:
      return type === FilterValueType.rangeDate ? `is` : `between`
    case `in_the_past`:
      return `In the past`
    case `in_the_future`:
      return `In the future`
    case `gte`:
      return `≥`
    case `lte`:
      return `≤`
  }
}

export function getReadableValue(value: FilterValue, type: FilterValueType, operator: FilterOperator): string {
  if (typeof value === 'undefined' || !value || compact(value).length === 0) {
    return ''
  }
  if (
    (type === FilterValueType.number || type === FilterValueType.age) &&
    operator !== `between` &&
    typeof value === `string`
  ) {
    return value.replace(/'/g, ``).split(`,`)[0]
  }
  if (Array.isArray(value)) {
    if (operator === `between`) {
      return value.join(` and `)
    }
    return value.length > 1 ? `(${value.length})` : ``
  }
  if (type === FilterValueType.boolean) {
    switch (value) {
      case `true`:
        return `Active`
      case `false`:
        return `Inactive`
      default:
        return value
    }
  }
  return value
}

export function getDateRangeValues(selectorPreset: DateRangeOperator): [Moment, Moment] {
  let fromDate: Moment | null = moment()
  let toDate: Moment | null = moment()
  switch (selectorPreset) {
    case DateRangeOperator.yesterday:
      fromDate = moment().subtract(1, `day`).startOf(`day`)
      toDate = moment().subtract(1, `day`).endOf(`day`)
      break
    case DateRangeOperator.today:
      fromDate = moment().startOf(`day`)
      toDate = moment()
      break
    case DateRangeOperator.this_week:
      fromDate = moment().startOf(`week`)
      toDate = moment().endOf(`week`)
      break
    case DateRangeOperator.last_week:
      fromDate = moment().subtract(1, `week`).startOf(`week`)
      toDate = moment().subtract(1, `week`).endOf(`week`)
      break
    case DateRangeOperator.this_month:
      fromDate = moment().startOf(`month`)
      toDate = moment().endOf(`month`)
      break
    case DateRangeOperator.last_month:
      fromDate = moment().subtract(1, `month`).startOf(`month`)
      toDate = moment().subtract(1, `month`).endOf(`month`)
      break
    case DateRangeOperator.last_30_days:
      fromDate = moment().subtract(30, `days`).startOf(`day`)
      toDate = moment()
      break
    case DateRangeOperator.last_90_days:
      fromDate = moment().subtract(90, `days`).startOf(`day`)
      toDate = moment()
      break
    case DateRangeOperator.next_30_days:
      fromDate = moment().startOf(`day`)
      toDate = moment().add(30, `days`).endOf(`day`)
      break
    case DateRangeOperator.next_90_days:
      fromDate = moment().startOf(`day`)
      toDate = moment().add(90, `days`).endOf(`day`)
      break
    case DateRangeOperator.last_6_months:
      fromDate = moment().subtract(6, `months`).startOf(`month`)
      toDate = moment()
      break
    case DateRangeOperator.last_12_months:
      fromDate = moment().subtract(12, `months`).startOf(`month`)
      toDate = moment()
      break
    case DateRangeOperator.this_year:
      fromDate = moment().startOf(`year`)
      toDate = moment()
      break
    case DateRangeOperator.last_year:
      fromDate = moment().subtract(1, `year`).startOf(`year`)
      toDate = moment().subtract(1, `year`).endOf(`year`)
      break
    default:
      break
  }
  return [fromDate, toDate]
}

export function encodeFilters(filters: FilterObject[]): string {
  return filters
    .filter((filter) => {
      if (filter.operator === `in` || filter.operator === `not_in`) {
        return Array.isArray(filter.value) ? compact(filter.value).length > 0 : !!filter.value
      }
      return true
    })
    ?.map(
      (filter) =>
        `${filter.attribute} ${filter.operator} ${
          Array.isArray(filter.value)
            ? `(${filter.value?.map((val) => `'${val}'`).join(`,`) ?? ``})`
            : `'${filter.value ?? ``}'`
        }`,
    )
    .join(FILTER_SEPARATOR)
}

export function decodeFilters(filterFromUrl: string | null | undefined): FilterObject[] {
  const getPartsFromFiltersString = (filters: string) => {
    const indexOfFirstSpace = filters.indexOf(' ')
    const indexOfSecondSpace = filters.indexOf(' ', indexOfFirstSpace + 1)
    const attribute = filters.slice(0, indexOfFirstSpace)
    const operator = filters.slice(indexOfFirstSpace + 1, indexOfSecondSpace) as FilterOperator
    const valueSlot = filters.slice(indexOfSecondSpace + 1)
    return [attribute, operator, valueSlot]
  }
  if (!filterFromUrl) {
    return []
  }
  return filterFromUrl.split(FILTER_SEPARATOR).map((filter) => {
    const [attribute, operator, valueSlot] = getPartsFromFiltersString(filter)
    const decodedValue = valueSlot.replace(/'/g, ``)
    if (operator === `between` && isIndefiniteDatePreset(decodedValue)) {
      return { attribute, operator, value: decodedValue } as FilterObject
    }
    if (operator === `between` && Object.values(DateRangeOperator).includes(DateRangeOperator[decodedValue])) {
      const value = getDateRangeValues(DateRangeOperator[decodedValue])
      return { attribute, operator, value: value.map((v) => moment(v).format(`YYYY-MM-DD`)) } as FilterObject
    } else if (
      operator === `in` ||
      operator === `not_in` ||
      operator === `between` ||
      operator === `in_the_past` ||
      operator === `in_the_future`
    ) {
      const value = valueSlot
        .slice(1, valueSlot.length - 1)
        .split(`,`)
        ?.map((wrappedVal) => wrappedVal.slice(1, wrappedVal.length - 1))
      return { attribute, operator, value } as FilterObject
    }
    return { attribute, operator, value: valueSlot.slice(1, valueSlot.length - 1) } as FilterObject
  })
}
/** @deprecated Recall intervals kept for daysPastDue filter backward compat. */
export const PERIO_INTERVAL_DAYS = 120
export const PROPHY_INTERVAL_DAYS = 181

export function convertFilterToESQueryString(filter: FilterObject, column?: FilterableColumn): string {
  let sanitizedValue = sanitizeValue(filter.value)
  const normalizeScalarValue = (value: string) => value.replace(/^'+|'+$/g, ``)
  const normalizedValue = Array.isArray(sanitizedValue)
    ? sanitizedValue.map((value) => normalizeScalarValue(value))
    : normalizeScalarValue(sanitizedValue)
  const normalizedScalarValue = Array.isArray(normalizedValue) ? undefined : normalizedValue
  const isBooleanScalar = normalizedScalarValue === `true` || normalizedScalarValue === `false`

  // Callers that set the operator directly. The UI instead sends these presets as the
  // value of a `between` filter, handled in the hygieneDueDate block further down.
  if (filter.operator === `in_the_past` || filter.operator === `in_the_future`) {
    const today = moment().format(`YYYY-MM-DD`)
    const isFuture = filter.operator === `in_the_future`

    if (isFuture) {
      if (Array.isArray(filter.value) && filter.value[1]) {
        return `${filter.attribute}:[${today} TO ${filter.value[1]}]`
      }

      return `${filter.attribute}:>${today}`
    }

    if (Array.isArray(filter.value) && filter.value[0]) {
      return `${filter.attribute}:[${filter.value[0]} TO ${today}]`
    }

    return `${filter.attribute}:<${today}`
  }

  // Check for empty values first
  const isEmptyValue = isValueEmpty(normalizedValue as string | string[])

  // If no value but has subQuery, treat as "match all" for that attribute
  if (isEmptyValue && column?.subQuery) {
    const result = `${filter.attribute}:*`
    return `(${result} AND ${column.subQuery})`
  }

  // Defensive guard: malformed or partially-hydrated filters should not generate
  // invalid ES queries like `field:""`.
  if (isEmptyValue && ![`attribute_exists`, `attribute_not_exists`].includes(filter.operator)) {
    return ``
  }

  // NOTE: this contains a hack to make the attribute_exists operator work with the way we're using it.
  // If the value is false, we want to negate the es query so that it returns objects that don't have the attribute.
  const base = `${
    filter.operator.includes(`not`) || (filter.operator === 'attribute_exists' && sanitizedValue === 'false') ? `!` : ``
  }${filter.attribute}:`
  // hygieneDueDate is now a stored field — query it directly
  if (filter.attribute === `hygieneDueDate`) {
    const value = sanitizedValue
    const today = moment().format(`YYYY-MM-DD`)
    if (value === `in_the_past`) {
      return `hygieneDueDate:[* TO ${today}]`
    }
    if (value === `in_the_future`) {
      return `hygieneDueDate:>${today}`
    }
    if (value === `today_or_later`) {
      return `hygieneDueDate:>=${today}`
    }
    if (value === `*`) {
      return `hygieneDueDate:*`
    }
    if (typeof value === 'string' && Object.values(DateRangeOperator).includes(DateRangeOperator[value as string])) {
      const [from, to] = getDateRangeValues(DateRangeOperator[value as string])
      return `hygieneDueDate:[${from.format(`YYYY-MM-DD`)} TO ${to.format(`YYYY-MM-DD`)}]`
    }
    if (Array.isArray(value)) {
      return `hygieneDueDate:[${value[0]} TO ${value[1]}]`
    }
    return ''
  }

  // The open-ended presets ride along as the value of a `between` filter, so they never
  // reach the range handling below. Both bounds are resolved against the current date.
  if (isIndefiniteDatePreset(normalizedScalarValue)) {
    const today = moment().format(`YYYY-MM-DD`)
    if (normalizedScalarValue === `in_the_future`) return `${filter.attribute}:>${today}`
    if (normalizedScalarValue === `today_or_later`) return `${filter.attribute}:>=${today}`
    return `${filter.attribute}:<${today}`
  }

  if (filter.attribute === `daysPastDue`) {
    const days = parseInt(normalizedScalarValue || '0', 10)
    const today = moment()

    if (filter.operator === `greater_than`) {
      // daysPastDue > X means hygieneDueDate < today - X days
      const date = today.clone().subtract(days, 'days').format('YYYY-MM-DD')
      return `hygieneDueDate:<${date}`
    }

    if (filter.operator === `greater_equal_than`) {
      // daysPastDue >= X means hygieneDueDate <= today - X days
      const date = today.clone().subtract(days, 'days').format('YYYY-MM-DD')
      return `hygieneDueDate:<=${date}`
    }

    if (filter.operator === `lesser_than`) {
      // daysPastDue < X means hygieneDueDate > today - X days
      const date = today.clone().subtract(days, 'days').format('YYYY-MM-DD')
      return `hygieneDueDate:>${date}`
    }

    if (filter.operator === `exactly`) {
      // daysPastDue == X means hygieneDueDate == today - X days
      const date = today.clone().subtract(days, 'days').format('YYYY-MM-DD')
      return `hygieneDueDate:${date}`
    }

    if (filter.operator === `between`) {
      // daysPastDue between X and Y means hygieneDueDate between (today - Y) and (today - X)
      const values = Array.isArray(normalizedValue) ? normalizedValue : [normalizedValue]
      if (values.length === 2) {
        const minDays = parseInt(values[0], 10)
        const maxDays = parseInt(values[1], 10)
        const from = today.clone().subtract(maxDays, 'days').format('YYYY-MM-DD')
        const to = today.clone().subtract(minDays, 'days').format('YYYY-MM-DD')
        return `hygieneDueDate:[${from} TO ${to}]`
      }
    }

    return ''
  }

  if (filter.attribute === `isPerio`) {
    const values = Array.isArray(normalizedValue) ? normalizedValue : [normalizedValue]
    // If both true and false are selected, treat as "All" (no filter)
    if (values.includes(`true`) && values.includes(`false`)) {
      return ``
    }
    if (values.includes(`false`)) {
      return `!isPerio:true`
    }
    if (values.includes(`true`)) {
      return `isPerio:true`
    }
  }

  if (filter.attribute === `birthDate`) {
    if (filter.operator === `between`) {
      sanitizedValue = (sanitizedValue as Array<string>).map((value, i) => {
        return moment()
          .subtract(+value + i, 'years')
          .format('YYYY-MM-DD')
      })
    } else {
      let difference = filter.operator === `greater_than` ? 1 : 0
      sanitizedValue = moment()
        .subtract(+sanitizedValue + difference, 'years')
        .format('YYYY-MM-DD')
    }
  }

  // Check for empty values after birthDate processing
  const isEmptyValueAfterProcessing = isValueEmpty(sanitizedValue)

  if (isEmptyValueAfterProcessing) {
    if (filter.operator.includes(`not`)) {
      return `NOT _exists_:${filter.attribute}`
    } else return ''
  }

  // Special handling for type filter with call directions (Call:inbound, Call:outbound)
  if (
    filter.attribute === 'type' &&
    (filter.operator === 'in' || filter.operator === 'not_in') &&
    Array.isArray(sanitizedValue)
  ) {
    const hasCallDirection = sanitizedValue.some((val) => val.startsWith('Call:'))
    if (hasCallDirection) {
      const parts = sanitizedValue.map((val) => {
        if (val.startsWith('Call:')) {
          const direction = val.split(':')[1]
          return `(type:Call AND subType:${direction})`
        }
        return `type:"${val}"`
      })
      const query = `(${parts.join(' OR ')})`
      const result = filter.operator === 'not_in' ? `NOT ${query}` : query
      return column?.subQuery ? `(${result} AND ${column.subQuery})` : result
    }
  }

  const opAndValue: string = (() => {
    switch (filter.operator) {
      case `contains`:
      case `not_contains`:
        return normalizedScalarValue!.includes(` `) ? `"${normalizedScalarValue}"` : `*${normalizedScalarValue}*`
      case `greater_than`:
        return `${filter.attribute === 'birthDate' ? '<' : '>'}${
          filter.attribute === 'birthDate' ? sanitizedValue : normalizedScalarValue
        }`
      case `greater_equal_than`:
        return `${filter.attribute === 'birthDate' ? '<=' : '>='}${
          filter.attribute === 'birthDate' ? sanitizedValue : normalizedScalarValue
        }`
      case `lesser_than`:
        return `${filter.attribute === 'birthDate' ? '>' : '<'}${
          filter.attribute === 'birthDate' ? sanitizedValue : normalizedScalarValue
        }`
      case `between`:
        if (filter.attribute === `birthDate`) return `[${normalizedValue[1]} TO ${normalizedValue[0]}]`
        if (Object.values(DateRangeOperator).includes(DateRangeOperator[normalizedScalarValue as string])) {
          let values = getDateRangeValues(DateRangeOperator[normalizedScalarValue as string])
          return `[${moment(values[0]).format('YYYY-MM-DD')} TO ${moment(values[1]).format('YYYY-MM-DD')}]`
        } else if (moment.isDate(normalizedValue[0])) {
          return `[${moment(normalizedValue[0]).format('YYYY-MM-DD')} TO ${moment(normalizedValue[1]).format(
            'YYYY-MM-DD',
          )}]`
        }
        return `[${normalizedValue[0]} TO ${normalizedValue[1]}]`
      case `exactly`:
      case `not_exactly`:
        if (normalizedScalarValue === `Any`) return `*`
        return filter.attribute === 'birthDate'
          ? `>${moment(normalizedScalarValue, 'YYYY-MM-DD')
              .subtract(1, `year`)
              .format(`YYYY-MM-DD`)} AND ${base}<${normalizedScalarValue}`
          : normalizedScalarValue === `(NOT _exists_:${filter.attribute})`
            ? normalizedScalarValue
            : isBooleanScalar
              ? normalizedScalarValue
              : `"${normalizedScalarValue}"`
      case `in`:
      case `not_in`:
        return `${
          Array.isArray(normalizedValue) && sanitizeValue.length
            ? `(${normalizedValue
                ?.map((rawVal) => {
                  const val = normalizeScalarValue(rawVal)
                  return val === `(NOT _exists_:${filter.attribute})`
                    ? val
                    : val === `*`
                      ? val
                      : val === `true` || val === `false`
                        ? val
                        : `"${val}"`
                })
                .join(` OR `)})`
            : ``
        }`
      case `attribute_exists`:
      case `attribute_not_exists`:
        return `*`
      default:
        return ``
    }
  })()
  const result = base + opAndValue
  return column?.subQuery ? `(${result} AND ${column.subQuery})` : result
}

const PURELY_NEGATIVE_TERM = /^(!|NOT |-)/

export function convertUrlFiltersToES(
  filters: string | undefined | null,
  join: string | undefined | null = null,
  columns?: FilterableColumn[],
): string[] {
  let esTerms = decodeFilters(filters).flatMap((filter) => {
    const column = columns?.find((col) => col.attribute === filter.attribute)
    // Process filter if it has a value OR if the column has a subQuery OR if operator doesn't need a value
    return !!filter.value ||
      column?.subQuery ||
      filter.operator === `in_the_past` ||
      filter.operator === `in_the_future`
      ? compact([convertFilterToESQueryString(filter, column)])
      : []
  })
  if (join?.toLowerCase() === `or` && esTerms.length > 1) {
    // query_string matches nothing for a purely negative clause inside an OR group, which silently turns
    // Match any into Match all, so each negation gets a match-everything baseline to subtract from.
    const anyTerms = esTerms.map((term) => (PURELY_NEGATIVE_TERM.test(term) ? `(*:* AND ${term})` : term))
    // Grouped so the OR cannot bind across the terms a page ANDs around it.
    return [`(${anyTerms.join(` OR `)})`]
  }
  if (join && esTerms.length) {
    return [esTerms.join(` ${join.toUpperCase()} `)]
  }
  return esTerms
}

export function addDefaultFilterProperties(
  attribute: string,
  preAppliedFilters?: PredefinedFilter,
): Partial<FilterableColumn> | undefined {
  let defaultValueOperator = preAppliedFilters?.[attribute]
  if (defaultValueOperator) {
    return {
      defaultFilter: true,
      defaultValue: defaultValueOperator.value,
      defaultOperator: defaultValueOperator.operator,
      readOnly: defaultValueOperator.readOnly,
    }
  }
}
/**
 * This function takes the filters from the URL and transforms them into filters that can
 * be used to query the Campaign model.
 * Platform filters are transformed into clid filters. gclid for Google and fbclid for Facebook.
 * @param filters string that comes from the URL
 * @returns string that can be used to query the Campaign model
 */
export function translatePlatformFilter(filters: string | undefined | null) {
  let clidFilters = decodeFilters(filters).map((filter) => {
    if (filter.attribute === `platform`) {
      return { ...filter, attribute: filter.value === `google` ? `gclid` : `fbclid`, value: `*` }
    }
    return filter
  })
  return encodeFilters(clidFilters)
}

/**
 * Reads the `platform` filter value from a URL search params object. Shared by
 * the custom-page editors (CustomPageForm and useOpenCustomPageEditor), which
 * both pass this to the contacts filterable-columns builder.
 */
export function platformFilterValue(searchParams: URLSearchParams): string {
  return (
    (decodeFilters(searchParams.get(`filters`)).find((filter) => filter.attribute === `platform`)?.value as string) ??
    ``
  )
}
